/**
 * Bundle PDP — "Build your own bundle"
 *
 * Markup lives in snippets/c-bundle-form.liquid. A bundle is a parent product
 * (price + content) plus up to four part slots (daphne.bundle_part_1 … _4).
 * Every product inside a slot is one colourway of the same style, so picking a
 * swatch swaps the whole product — image, "View Product" link and the set of
 * sizes/variant ids the form will submit.
 *
 * Add to cart posts the parent variant plus one line per part, each carrying
 * `parent_id` so Shopify nests them under the parent as bundle components:
 * https://shopify.dev/docs/apps/build/product-merchandising/nested-cart-lines
 *
 * This file is loaded by sections/product-hero.liquid only on bundle products.
 */
(function () {
	"use strict";

	var FORM = ".js-bundle-form";
	var PART = ".js-bundle-part";

	/**
	 * The selection label ("Selected: WHITE") is rendered by CSS from these two
	 * attributes — data-label wins while hovering, data-label-current otherwise.
	 * See _c-item-variants.scss.
	 */
	function setLabelCurrent(labelEl, value) {
		if (labelEl) labelEl.dataset.labelCurrent = value || "";
	}

	function setLabelHover(labelEl, value) {
		if (labelEl) labelEl.dataset.label = value || "";
	}

	function parsePartData(partEl) {
		var dataEl = partEl.querySelector(".js-bundle-part-data");

		if (!dataEl) return null;

		try {
			return JSON.parse(dataEl.textContent);
		} catch (error) {
			console.error("Bundle: could not parse part data", error);
			return null;
		}
	}

	function getSelectedColor(partEl, data) {
		var checked = partEl.querySelector(".js-bundle-color:checked");

		if (!checked) return null;

		return data.colors[parseInt(checked.dataset.colorIndex, 10)] || null;
	}

	function findVariant(color, size) {
		if (!color) return null;

		for (var i = 0; i < color.variants.length; i += 1) {
			if (color.variants[i].size === size) return color.variants[i];
		}

		return null;
	}

	/** The variant id this part will contribute to the cart, or "" if incomplete. */
	function resolveVariant(partEl, data) {
		var color = getSelectedColor(partEl, data);
		var sizeInput = partEl.querySelector(".js-bundle-size:checked");
		var sizeInputs = partEl.querySelectorAll(".js-bundle-size");
		var variant;

		if (!color) return null;

		// No size row at all — the colourway has a single variant.
		if (!sizeInputs.length) {
			variant = color.variants[0];
			return variant && variant.available ? variant : null;
		}

		if (!sizeInput) return null;

		variant = findVariant(color, sizeInput.value);

		return variant && variant.available ? variant : null;
	}

	function syncPart(partEl, data, options) {
		var color = getSelectedColor(partEl, data);
		var thumb = partEl.querySelector(".js-bundle-part-thumb");
		var link = partEl.querySelector(".js-bundle-part-link");
		var colorLabel = partEl.querySelector(".js-bundle-color-label");
		var sizeLabel = partEl.querySelector(".js-bundle-size-label");
		var sizeInputs = partEl.querySelectorAll(".js-bundle-size");
		var variantInput = partEl.querySelector(".js-bundle-part-variant");
		var checkedSize = partEl.querySelector(".js-bundle-size:checked");
		var firstAvailable = null;
		var variant;

		if (!color) return;

		if (thumb && color.image && thumb.getAttribute("src") !== color.image) {
			thumb.src = color.image;
			// The Liquid srcset is for the initial colourway only.
			thumb.removeAttribute("srcset");
		}

		if (link) link.href = color.url;
		setLabelCurrent(colorLabel, color.name);

		// Sizes are the union across every colourway in the slot, so the row keeps a
		// stable width. Disable the ones this colourway doesn't sell.
		Array.prototype.forEach.call(sizeInputs, function (input) {
			var sizeVariant = findVariant(color, input.value);
			var isAvailable = !!(sizeVariant && sizeVariant.available);

			input.disabled = !isAvailable;

			if (isAvailable && !firstAvailable) firstAvailable = input;

			// The size they had picked isn't sold in this colourway — drop it rather
			// than silently swapping them into a size they didn't choose.
			if (input.checked && !isAvailable) input.checked = false;
		});

		if (
			options &&
			options.preselect &&
			!partEl.querySelector(".js-bundle-size:checked") &&
			firstAvailable
		) {
			firstAvailable.checked = true;
		}

		checkedSize = partEl.querySelector(".js-bundle-size:checked");
		setLabelCurrent(sizeLabel, checkedSize ? checkedSize.dataset.label : "");

		variant = resolveVariant(partEl, data);
		if (variantInput) variantInput.value = variant ? variant.id : "";

		partEl.dataset.complete = variant ? "true" : "false";
	}

	function isComplete(form) {
		var parts = form.querySelectorAll(PART);

		for (var i = 0; i < parts.length; i += 1) {
			if (parts[i].dataset.complete !== "true") return false;
		}

		return parts.length > 0;
	}

	function setError(form, message) {
		var errorEl = form.querySelector(".js-bundle-error");

		if (!errorEl) return;

		errorEl.textContent = message || "";
		errorEl.hidden = !message;
	}

	function syncForm(form, options) {
		Array.prototype.forEach.call(form.querySelectorAll(PART), function (partEl) {
			var data = partEl.bundleData;

			if (data) syncPart(partEl, data, options);
		});

		form.dataset.complete = isComplete(form) ? "true" : "false";
	}

	/**
	 * Shopify derives a line item key from the variant id plus a hash of the line's
	 * properties, and ignores what's nested underneath. Without this, two bundles
	 * built from different parts share a parent variant, hash identically, and
	 * collapse into one line of quantity 2.
	 *
	 * The key is derived from the chosen parts rather than randomised, so two
	 * genuinely identical bundles still stack as quantity 2 instead of adding a
	 * second line. Swap in a random value if every add should be its own line.
	 * Underscore-prefixed, so it stays hidden from the customer and the order
	 * confirmation while remaining readable to the cart functions and the 3PL.
	 */
	function bundleKey(form) {
		var parts = [];

		Array.prototype.forEach.call(form.querySelectorAll(PART), function (partEl) {
			var variantInput = partEl.querySelector(".js-bundle-part-variant");

			parts.push((variantInput && variantInput.value) || "0");
		});

		return form.dataset.bundleVariantId + "-" + parts.join("-");
	}

	/**
	 * Cart properties the form declares as hidden inputs, keyed by the name inside
	 * properties[...]. Liquid owns the names and their translations, so anything
	 * added there is carried without touching this file.
	 *
	 * Read here rather than left to the browser because the bundle posts a JSON
	 * payload to /cart/add.js; a hidden input is only submitted automatically by a
	 * native form post, which this form never does.
	 */
	function formProperties(form) {
		var props = {};
		var prefix = "properties[";

		Array.prototype.forEach.call(
			form.querySelectorAll('input[name^="properties["]'),
			function (input) {
				var name = input.name.slice(prefix.length, -1);

				if (name && input.value) props[name] = input.value;
			}
		);

		return props;
	}

	/** Parent line first, then one nested line per part. */
	function buildItems(form) {
		var quantityInput = form.querySelector('input[name="quantity"]');
		var quantity = parseInt(quantityInput ? quantityInput.value : 1, 10) || 1;
		var parentId = parseInt(form.dataset.bundleVariantId, 10);
		var properties = formProperties(form);
		var items;

		properties._bundle_key = bundleKey(form);

		// One object shared by every line: built once, then serialised; don't mutate after this point.
		items = [{ id: parentId, quantity: quantity, properties: properties }];

		Array.prototype.forEach.call(form.querySelectorAll(PART), function (partEl) {
			var variantInput = partEl.querySelector(".js-bundle-part-variant");

			if (!variantInput || !variantInput.value) return;

			items.push({
				id: parseInt(variantInput.value, 10),
				quantity: quantity,
				parent_id: parentId,
				properties: properties,
			});
		});

		return items;
	}

	function firstIncompletePart(form) {
		return form.querySelector('.js-bundle-part[data-complete="false"]');
	}

	function submit(form) {
		var submitButton = form.querySelector(".js-bundle-submit");
		var incomplete;

		setError(form, "");

		if (!isComplete(form)) {
			incomplete = firstIncompletePart(form);
			setError(
				form,
				incomplete
					? "Please select a size for " + incomplete.dataset.partLabel + "."
					: "Please complete your bundle."
			);
			if (incomplete) {
				incomplete.scrollIntoView({ behavior: "smooth", block: "center" });
			}
			return;
		}

		if (form.dataset.submitting === "true") return;
		form.dataset.submitting = "true";
		if (submitButton) submitButton.disabled = true;

		fetch(form.dataset.action, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
			},
			body: JSON.stringify({ items: buildItems(form) }),
		})
			.then(function (response) {
				return response.json().then(function (data) {
					return { ok: response.ok, data: data };
				});
			})
			.then(function (result) {
				if (!result.ok) {
					setError(
						form,
						result.data.description ||
							result.data.message ||
							"Sorry, this bundle could not be added to your cart."
					);
					return;
				}

				// Reuse the theme's cart plumbing when it's on the page.
				if (typeof cCart !== "undefined") {
					cCart.updateCartContent();
					cCart.minicartOpen();
				} else {
					window.location.href = window.Shopify.routes.root + "cart";
				}
			})
			.catch(function (error) {
				console.error("Bundle: add to cart failed", error);
				setError(form, "Sorry, this bundle could not be added to your cart.");
			})
			.then(function () {
				form.dataset.submitting = "false";
				if (submitButton) submitButton.disabled = false;
			});
	}

	function initSizeGuides() {
		var close = function (panel) {
			if (!panel) return;
			panel.classList.remove("is-active");
			panel.setAttribute("aria-hidden", "true");
		};

		var closeAll = function () {
			Array.prototype.forEach.call(
				document.querySelectorAll(".js-bundle-size-guide"),
				close
			);
		};

		document.addEventListener("click", function (e) {
			var trigger = e.target.closest(".js-bundle-size-guide-open");
			var panel;

			if (trigger) {
				panel = document.getElementById(trigger.dataset.sizeGuideTarget);
				closeAll();
				if (panel) {
					panel.classList.add("is-active");
					panel.setAttribute("aria-hidden", "false");
				}
				return;
			}

			if (e.target.closest(".js-bundle-size-guide-close")) {
				closeAll();
				return;
			}

			// Click anywhere outside an open panel dismisses it.
			if (!e.target.closest(".js-bundle-size-guide")) closeAll();
		});

		document.addEventListener("keydown", function (e) {
			if (e.key === "Escape") closeAll();
		});
	}

	function initForm(form) {
		Array.prototype.forEach.call(form.querySelectorAll(PART), function (partEl) {
			partEl.bundleData = parsePartData(partEl);
		});

		// Preselect the first in-stock size so the form starts in a usable state.
		syncForm(form, { preselect: true });

		form.addEventListener("change", function (e) {
			var target = e.target;

			if (
				target.classList.contains("js-bundle-color") ||
				target.classList.contains("js-bundle-size")
			) {
				setError(form, "");
				syncForm(form);
			}
		});

		// Hover preview of the swatch/pill about to be picked.
		form.addEventListener("mouseover", function (e) {
			var input = e.target.closest(".js-bundle-color, .js-bundle-size");
			var partEl = e.target.closest(PART);
			var isColor;

			if (!input || !partEl) return;

			isColor = input.classList.contains("js-bundle-color");
			setLabelHover(
				partEl.querySelector(
					isColor ? ".js-bundle-color-label" : ".js-bundle-size-label"
				),
				input.dataset.label
			);
		});

		form.addEventListener("mouseout", function (e) {
			var input = e.target.closest(".js-bundle-color, .js-bundle-size");
			var partEl = e.target.closest(PART);
			var isColor;

			if (!input || !partEl) return;

			isColor = input.classList.contains("js-bundle-color");
			setLabelHover(
				partEl.querySelector(
					isColor ? ".js-bundle-color-label" : ".js-bundle-size-label"
				),
				""
			);
		});

		form.addEventListener("submit", function (e) {
			e.preventDefault();
			submit(form);
		});
	}

	function init() {
		var forms = document.querySelectorAll(FORM);

		if (!forms.length) return;

		Array.prototype.forEach.call(forms, initForm);
		initSizeGuides();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();